import { alpha, SxProps, Theme } from '@mui/material';

export const DISPATCH = {
  bg: '#0f172a',
  bgSoft: 'linear-gradient(180deg, #0f172a 0%, #1e293b 40%, #f1f5f9 40%, #f8fafc 100%)',
  bgPanel: '#ffffff',
  bgMuted: '#f8fafc',
  bgElevated: '#ffffff',
  border: 'rgba(15, 23, 42, 0.08)',
  borderStrong: 'rgba(15, 23, 42, 0.14)',
  shadow: '0 1px 3px rgba(15, 23, 42, 0.06), 0 8px 24px rgba(15, 23, 42, 0.06)',
  shadowLg: '0 8px 32px rgba(15, 23, 42, 0.12)',
  shadowSelected: '0 0 0 2px rgba(99, 102, 241, 0.35), 0 12px 32px rgba(99, 102, 241, 0.15)',
  primary: '#6366f1',
  primaryDark: '#4f46e5',
  primaryLight: '#818cf8',
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
  headerGradient: 'linear-gradient(135deg, #0f172a 0%, #1e293b 55%, #312e81 100%)',
  radius: 14,
  radiusSm: 10,
  radiusLg: 18,
  queueWidth: { xs: '100%', md: 380, lg: 420, xl: 440 },
  detailWidth: { xs: '100%', md: 400, lg: 440, xl: 480 },
  toolbarHeight: { xs: 56, sm: 64 },
} as const;

export function panelShell(extra?: Record<string, unknown>): SxProps<Theme> {
  return {
    bgcolor: DISPATCH.bgPanel,
    borderRadius: `${DISPATCH.radius}px`,
    border: `1px solid ${DISPATCH.border}`,
    boxShadow: DISPATCH.shadow,
    overflow: 'hidden',
    ...extra,
  };
}

export function sectionHeader(title: string, subtitle?: string) {
  return {
    px: 2,
    py: 1.5,
    borderBottom: `1px solid ${DISPATCH.border}`,
    bgcolor: DISPATCH.bgElevated,
    title,
    subtitle,
  };
}

export function bookFieldSx(accent = DISPATCH.primary): SxProps<Theme> {
  return {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      bgcolor: '#fff',
      minHeight: 36,
      '& fieldset': { borderColor: '#e2e8f0' },
      '&:hover fieldset': { borderColor: '#cbd5e1' },
      '&.Mui-focused': {
        bgcolor: '#fff',
        '& fieldset': { borderColor: `${accent} !important`, borderWidth: '1.5px' },
      },
    },
    '& .MuiOutlinedInput-input': {
      py: '7px',
      fontSize: '0.8125rem',
    },
    '& .MuiInputLabel-root': { fontWeight: 600, fontSize: '0.75rem' },
    '& .MuiInputLabel-root.Mui-focused': { color: accent },
    '& .MuiFormHelperText-root': { fontSize: '0.68rem', mt: 0.35 },
  };
}

export function bookSegSx(accent: string): SxProps<Theme> {
  return {
    p: '2px',
    bgcolor: '#f1f5f9',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    '& .MuiToggleButtonGroup-grouped': {
      border: '0 !important',
      borderRadius: '6px !important',
      margin: 0,
    },
    '& .MuiToggleButton-root': {
      flex: 1,
      py: 0.45,
      px: 1,
      gap: 0.4,
      textTransform: 'none',
      fontWeight: 700,
      fontSize: '0.72rem',
      color: '#64748b',
      '&.Mui-selected': {
        bgcolor: '#fff',
        color: accent,
        boxShadow: '0 1px 2px rgba(15,23,42,0.06)',
        '&:hover': { bgcolor: '#fff' },
      },
      '&:hover': { bgcolor: 'rgba(255,255,255,0.7)' },
    },
  };
}

export function bookChoiceSx(selected: boolean, accent: string): SxProps<Theme> {
  return {
    cursor: 'pointer',
    borderRadius: '8px',
    border: '1px solid',
    borderColor: selected ? accent : '#e2e8f0',
    bgcolor: selected ? alpha(accent, 0.06) : '#fff',
    boxShadow: 'none',
    transition: 'border-color 0.12s ease, background-color 0.12s ease',
    '&:hover': {
      borderColor: selected ? accent : '#cbd5e1',
      bgcolor: selected ? alpha(accent, 0.08) : '#f8fafc',
    },
  };
}

export function bookPanelSx(extra?: Record<string, unknown>): SxProps<Theme> {
  return {
    bgcolor: '#fff',
    borderRadius: '10px',
    border: '1px solid #e2e8f0',
    boxShadow: 'none',
    overflow: 'hidden',
    ...extra,
  };
}

export const dispatchScroll = {
  flex: 1,
  minHeight: 0,
  minWidth: 0,
  width: '100%',
  maxWidth: '100%',
  overflowY: 'auto' as const,
  overflowX: 'hidden' as const,
  WebkitOverflowScrolling: 'touch' as const,
  boxSizing: 'border-box' as const,
};

export const dispatchPageBleed = {
  mx: 0,
  mt: 0,
  mb: 0,
};

export const dispatchPageHeight = {
  height: { xs: 'calc(100dvh - 48px)', sm: 'calc(100dvh - 52px)' },
  minHeight: 520,
  maxHeight: { xs: 'calc(100dvh - 48px)', sm: 'calc(100dvh - 52px)' },
};
